import {html, LitElement, nothing, render as litRender} from "lit";
import {mix} from "mixwith";
import MutableArrayMixin from "./Mixins/MutableArrayMixin.js";
import {repeat} from "lit/directives/repeat.js";
import {classMap} from "lit/directives/class-map.js";
import {styleMap} from "lit/directives/style-map.js";
import {ifDefined} from "lit/directives/if-defined.js";
import AuiTableStyle from "./aui-table.scss";
import {getValueByPath} from "../Utils/ObjectUtils.js";

/**
 * @typedef {Object} ColumnCellEvents
 * @property {function(column:TableColumn, event:MouseEvent):void} click
 */

/**
 * @typedef {Object} RowCellEvents
 * @property {function(row:TableRow, event:MouseEvent):void} click
 */

/**
 * @typedef {Object} TableColumn
 * @property {string} key The item key from which to get the value from, which will be displayed.
 * @property {string} label The label of the column which will be displayed.
 * @property {string} align The horizontal alignment of the cells content. (left|right|center) default=left
 * @property {string} valign The vertical alignment of the cells content. (top|bottom|center) default=top
 * @property {string} direction The cells content flow direction. (right|down) default=right
 * @property {string} size The width of the cells, using grid-template-columns values. default=this.defaultColumnSize
 *
 * @property {ClassInfo|function(column: TableColumn): ClassInfo} columnCellClasses The classes applied to the column.
 * @property {StyleInfo|function(column: TableColumn): StyleInfo} columnCellStyle The style applied to the column.
 * @property {ColumnCellEvents} columnCellEvents Events for the columns.
 *
 * @property {ClassInfo|function(row: TableRow): ClassInfo} rowCellClasses The classes applied to all rows of the column.
 * @property {StyleInfo|function(row: TableRow): StyleInfo} rowCellStyle The style applied to all rows of the column.
 * @property {function(column:TableColumn, columnIndex:number):TemplateResult} renderColumnCell A custom column cell renderer.
 * @property {function(row:TableRow):TemplateResult} renderRowCell A custom row cell renderer of the column.
 * @property {RowCellEvents} rowCellEvents Events for all the rows of the column.
 */

/**
 * @typedef {Object} TableRow
 * @property {object} item
 * @property {number} itemIndex
 * @property {*} cellValue Only available in renderRowCell
 * @property {TableColumn} column
 * @property {number} columnIndex
 */

/**
 * @typedef {ClassInfo & Object} TableCellClassMap
 * @property {boolean} first
 * @property {boolean} last
 * @property {boolean} align-right
 * @property {boolean} align-center
 * @property {boolean} flow-down
 */

/**
 * An table using CSS-Grid.
 *
 * Sorting
 * Column Selection
 * Filtering
 * Loading Data
 */
export class AuiTable extends mix(LitElement).with(MutableArrayMixin) {

	static get properties() {
		return {
			columns: Array,
			hideColumns: Boolean
		};
	}

	set columns(value) {
		const oldValue = this.__columns;
		this.__columns = value;
		this.updateGridColumns();
		this.requestUpdate("columns", oldValue);
	}

	get columns() {
		return this.__columns;
	}

	constructor() {
		super();
		this._tableStyle = {};
		this.defaultColumnSize = "auto";

		this.columns = [];
	}

	updateGridColumns() {
		this._tableStyle.gridTemplateColumns =
			this.columns.length > 0 ? this.columns.map(column => column.size || this.defaultColumnSize).join(" ") : null;
	}

	removeByIndex(index) {
		this.items.splice(index, 1);
		this.requestUpdate();
	}

	/**
	 * Returns the row element rendered on the corresponding index.
	 *
	 * @param {number} index The row index
	 * @return {Element|null}
	 */
	getRowElementByIndex(index) {
		if(index >= 0 && index < this.items.length)
			return this.shadowRoot.querySelector(`.grid-table > .row:nth-of-type(${index + 1})`);
		return null;
	}

	/**
	 * Returns the row cell element rendered on the corresponding row index and column key.
	 *
	 * @param {number} index The row index
	 * @param {string} key The column key
	 * @return {Element|null}
	 */
	getRowCellElementByIndex(index, key) {
		const cellIndex = this.columns.findIndex(column => column.key === key);
		if(cellIndex < 0)
			return null;

		const row = this.getRowElementByIndex(index);
		if(row === null)
			return null;

		return row.querySelector(`.cell:nth-of-type(${cellIndex + 1})`);
	}

	update(changedProperties) {
		// Setting properties in `render` should not trigger an update. Since
		// updates are allowed after super.update, it's important to call `render`
		// before that.
		const shadowTemplateResult = this.render();
		const lightTemplateResult = this.renderSlottedCells();

		// Update data
		super.update(changedProperties);

		// Render ShadowDOM
		litRender(shadowTemplateResult, this.renderRoot, {
			scopeName: this.localName,
			eventContext: this
		});

		// When adoptedStyles are not supported, insert styling after rendering to ensure adoptedStyles have highest priority.
		if(this._needsShimAdoptedStyleSheets) {
			this._needsShimAdoptedStyleSheets = false;
			this.constructor._styles.forEach((s) => {
				const style = document.createElement('style');
				style.textContent = s.cssText;
				this.renderRoot.appendChild(style);
			});
		}

		// Render LightDOM
		litRender(lightTemplateResult, this, { scopeName: this.localName, eventContext: this });
	}

	static get styles() {
		return AuiTableStyle;
	}

	/**
	 * Has to return the unique column key.
	 *
	 * @param {TableColumn} column
	 * @return {*}
	 * @protected
	 */
	_columnKeyFn(column) { return column.key; }

	/**
	 * Has to return the unique item ID.
	 *
	 * @param item
	 * @return {*}
	 * @protected
	 */
	_rowKeyFn(item) { return item.ID; }

	/**
	 * Returns the default classMap of an column and/or row cell.
	 *
	 * @param {TableColumn} column
	 * @param {number} columnIndex
	 * @return {TableCellClassMap}
	 */
	getCellClassMap(column, columnIndex) {
		const maxColumns = this.columns.length;

		/**
		 * Defaults to right if column.key is #
		 *
		 * @type {string|undefined}
		 */
		const align = column.align || column.key === "#" ? "right" : undefined;

		return {
			first: columnIndex === 0,
			last: columnIndex === maxColumns - 1,
			"align-right": align === "right",
			"align-center": align === "center",
			"valign-bottom": column.valign === "bottom",
			"valign-center": column.valign === "center",
			"flow-down": column.direction === "down"
		};
	}

	/**
	 * @param {TableColumn} column
	 * @param {number} columnIndex
	 * @return {TableCellClassMap}
	 */
	getColumnCellClassMap(column, columnIndex) {
		const classMap = typeof column.columnCellClasses === "function" ? column.columnCellClasses(column) : column.columnCellClasses;
		return Object.assign({}, this.getCellClassMap(column, columnIndex), classMap);
	}

	/**
	 * Returns the styleMap of an column cell.
	 *
	 * @param {TableColumn} column
	 * @return {StyleInfo}
	 */
	getColumnCellStyle(column) {
		return (typeof column.columnCellStyle === "function" ? column.columnCellStyle(column) : column.columnCellStyle) ?? {};
	}

	/**
	 * Creates the internal row events using the defined column events.
	 *
	 * @param {TableColumn} column
	 * @return {ColumnCellEvents}
	 * @protected
	 */
	getColumnCellEvents(column) {
		const events = {
			click: undefined
		};

		if(column.columnCellEvents) {
			if(typeof column.columnCellEvents.click === "function") {
				events.click = e => column.columnCellEvents.click(column, e);
			}
		}

		return events;
	}

	/**
	 * @param {TableRow} row
	 * @return {TableCellClassMap}
	 */
	getRowCellClassMap(row) {
		const classMap = typeof row.column.rowCellClasses === "function" ? row.column.rowCellClasses(row) : row.column.rowCellClasses;
		return Object.assign({}, this.getCellClassMap(row.column, row.columnIndex), classMap);
	}

	/**
	 * Returns the styleMap of an row cell.
	 *
	 * @param {TableRow} row
	 * @return {StyleInfo}
	 */
	getRowCellStyle(row) {
		return (typeof row.column.rowCellStyle === "function" ? row.column.rowCellStyle(row) : row.column.rowCellStyle) ?? {};
	}

	/**
	 * Creates the internal row events using the defined column events.
	 *
	 * @param {TableRow} row
	 * @return {RowCellEvents}
	 * @protected
	 */
	getRowCellEvents(row) {
		const events = {
			click: undefined
		};

		if(row.column.rowCellEvents) {
			if(typeof row.column.rowCellEvents.click === "function") {
				events.click = e => row.column.rowCellEvents.click(row, e);
			}
		}

		return events;
	}

	/**
	 * Returns the row cell value.
	 *
	 * @param row
	 * @return {*}
	 */
	getRowCellValue(row) {
		return row.column.key ? getValueByPath(row.column.key, row.item) : undefined;
	}

	/**
	 * Renders the table.
	 *
	 * @return {TemplateResult}
	 */
	render() {
		return html`
            <!-- Table -->
            <section class="grid-table" style=${styleMap(this._tableStyle)}>
                <!-- Columns -->
                ${this.hideColumns ? nothing : repeat(this.columns, this._columnKeyFn, (column, columnIndex) => this.renderColumnRoot(column, columnIndex))}

                <!-- Rows -->
                ${repeat(this.items, this._rowKeyFn, (item, itemIndex) => this.renderRow(item, itemIndex))}
            </section>
		`;
	}

	/**
	 * Renders a column.
	 *
	 * @param {TableColumn} column
	 * @param {int} columnIndex
	 * @return {TemplateResult}
	 */
	renderColumnRoot(column, columnIndex) {
		const events = this.getColumnCellEvents(column);

		return html`
            <div class="cell column${classMap(this.getColumnCellClassMap(column, columnIndex))}"
                 style=${styleMap(this.getColumnCellStyle(column))}
                 @click=${ifDefined(events.click)}>
                <slot name=${this.getColumnCellSlotName(columnIndex)}></slot>
            </div>
		`;
	}

	/**
	 * Renders a cell for each column.
	 *
	 * @param item
	 * @param {int} itemIndex
	 * @return {TemplateResult}
	 */
	renderRow(item, itemIndex) {
		return html`
            <section class="row">
                ${repeat(this.columns, this._columnKeyFn, (column, columnIndex) =>
                        this.renderRowCellRoot({
                            item: item,
                            itemIndex: itemIndex,
                            column: column,
                            columnIndex: columnIndex
                        }))}
            </section>
		`;
	}

	/**
	 * Renders a row cell.
	 *
	 * @param {TableRow} row
	 * @return {TemplateResult}
	 */
	renderRowCellRoot(row) {
		const events = this.getRowCellEvents(row);

		return html`
            <div class="cell row${classMap(this.getRowCellClassMap(row))}"
                 style=${styleMap(this.getRowCellStyle(row))}
                 @click=${ifDefined(events.click)}>
                <slot name=${this.getRowCellSlotName(row.itemIndex, row.columnIndex)}></slot>
            </div>
		`;
	}

	/*******************************************************************************************************************
	 * RENDERING OF LIGHT DOM
	 ******************************************************************************************************************/

	/**
	 * Returns the slot name of the column index.
	 *
	 * @param {int} columnIndex
	 * @return {string}
	 */
	getColumnCellSlotName(columnIndex) {
		return `column-cell-${columnIndex}`;
	}

	/**
	 * Returns the slot name of the row and column index.
	 *
	 * @param {int} rowIndex
	 * @param {int} columnIndex
	 * @return {string}
	 */
	getRowCellSlotName(rowIndex, columnIndex) {
		return `row-${rowIndex}-cell-${columnIndex}`;
	}

	/**
	 * Renders slotted cells.
	 *
	 * @return {TemplateResult}
	 */
	renderSlottedCells() {
		return html`
            <!-- Columns -->
            ${this.hideColumns ? nothing : repeat(this.columns, this._columnKeyFn, (column, columnIndex) => html`
                <section slot=${this.getColumnCellSlotName(columnIndex)}>${this.renderColumn(column, columnIndex)}</section>
            `)}

            <!-- Rows -->
            ${repeat(this.items, this._rowKeyFn, (item, itemIndex) => html`
                ${repeat(this.columns, this._columnKeyFn, (column, columnIndex) => html`
                    <section slot=${this.getRowCellSlotName(itemIndex, columnIndex)}>${this.renderRowCell({
                            item: item,
                            itemIndex: itemIndex,
                            column: column,
                            columnIndex: columnIndex
                        })}
                    </section>
                `)}
            `)}
		`;
	}

	/**
	 * Renders a column inner html.
	 *
	 * @param {TableColumn} column
	 * @param {int} columnIndex
	 * @return {TemplateResult}
	 */
	renderColumn(column, columnIndex) {
		if(typeof column.renderColumnCell === "function")
			return column.renderColumnCell(column, columnIndex);

		// Default
		return html`${ifDefined(column.label)}`;
	}

	/**
	 * Renders a row cell inner html.
	 *
	 * @param {TableRow} row
	 * @return {TemplateResult}
	 */
	renderRowCell(row) {
		row.cellValue = row.column.key === "#" ? (row.itemIndex + 1) : this.getRowCellValue(row);

		if(typeof row.column.renderRowCell === "function")
			return row.column.renderRowCell(row);

		// Default
		return html`${row.cellValue}`;
	}

}

export default AuiTable;

customElements.define("aui-table", AuiTable);